'use client';

import { createLogger } from '@ctrl-enter/tools/utils';
import '@radix-ui/themes/styles.css';
import {
  AdminPortalDomainVerification,
  AdminPortalSsoConnection,
  WorkOsWidgets,
} from '@workos-inc/widgets';
import '@workos-inc/widgets/styles.css';
import * as React from 'react';
import { useAuth } from '../auth';
import { useCheckChatAccessQuery } from '../hooks/use-gateway-queries';
import { useWidgetToken } from '../hooks/use-widget-token';
import {
  areTestToolsEnabled,
  setGlobalCheckAndRefresh,
} from '../hooks/use-widget-token-test-utils';
import { useToken, useTokenError, useTokenLoading } from '../stores/widget-token-store';
import '../styles/workos-overrides.css';
import { Button } from './button';
import { InfoSection } from './info-section';
import { OrganizationSettings } from './organization-settings';
import { WorkOsWidgetErrorBoundary } from './workos-widget-error-boundary';

const logger = createLogger('client:component:manage-team-panel');

/**
 * ManageTeamPanel renders org-level Security & Access settings: organization
 * details plus the WorkOS SSO connection and domain verification widgets.
 * Team member management (invite/remove/role) now lives in the Admin Dashboard.
 *
 * Uses automatic token lifecycle management with:
 * - Proactive refresh before expiry (T-5 minutes)
 * - Re-validation on page visibility/focus
 * - Automatic recovery from 403 errors
 */
const ManageTeamPanel = () => {
  const { user, isAuthenticated } = useAuth();

  // Initialize token lifecycle management (runs in background)
  const { refresh, checkAndRefresh } = useWidgetToken({
    refreshBeforeMinutes: 5,
    refreshOnVisibility: true,
    enableLogging: true, // Enable for debugging during initial rollout
  });

  // Get token state from centralized store
  const token = useToken();
  const isLoading = useTokenLoading();
  const error = useTokenError();

  // Fetch organization data for settings display
  const { data: chatAccessData, refetch: refetchChatAccess } = useCheckChatAccessQuery(
    isAuthenticated && !!user?.organizationId
  );

  const organizationData = chatAccessData?.body?.subscription;

  // Register checkAndRefresh for test panel (only if test tools are enabled)
  React.useEffect(() => {
    if (areTestToolsEnabled() && checkAndRefresh) {
      logger.info('🔵 [MANAGE-TEAM-PANEL] Registering checkAndRefresh with test tools');
      setGlobalCheckAndRefresh(checkAndRefresh);
    }
  }, [checkAndRefresh]);

  // Check if user has permission to manage users (must be before hooks)
  const canManageUsers = user?.role === 'admin';

  // Debug: Log token state changes (MUST be before any conditional returns)
  React.useEffect(() => {
    logger.info(
      '🔵 [MANAGE-TEAM-PANEL] Token state changed:',
      JSON.stringify(
        {
          hasToken: !!token,
          tokenLength: token?.length || 0,
          isLoading,
          hasError: !!error,
          errorMessage: error?.message,
          hasUser: !!user,
          canManageUsers,
        },
        null,
        2
      )
    );
  }, [token, isLoading, error, user, canManageUsers]);

  // Debug: Log when rendering widgets (MUST be before any conditional returns)
  React.useEffect(() => {
    if (token) {
      logger.info(
        '🟢 [MANAGE-TEAM-PANEL] Rendering WorkOS widgets with token:',
        JSON.stringify(
          {
            tokenLength: token.length,
            tokenPreview: `${token.substring(0, 50)}...${token.substring(token.length - 20)}`,
            widgets: ['AdminPortalSsoConnection', 'AdminPortalDomainVerification'],
          },
          null,
          2
        )
      );
    }
  }, [token]);

  // Auto-link customer functionality has been removed
  // There should only be one stripe customer per WorkOS organization that is created on WorkOS organization creation

  // Check if user exists
  if (!user) {
    return null;
  }

  // Show loading state only for admins (who need the token)
  if (canManageUsers && isLoading) {
    return (
      <InfoSection>
        <div className="flex items-center justify-center p-4">
          <div className="text-secondary-enhanced">Loading team management...</div>
        </div>
      </InfoSection>
    );
  }

  // Show error state with retry option (only for admins who need the token)
  if (canManageUsers && error && !token) {
    return (
      <InfoSection>
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="text-secondary-enhanced text-destructive">
            Failed to load team management: {error.message}
          </div>
          <Button onClick={() => refresh()} variant="primary" size="md">
            Retry
          </Button>
        </div>
      </InfoSection>
    );
  }

  // Show error if token generation failed (only for admins)
  if (canManageUsers && !token) {
    return (
      <div>
        <p>Failed to load team management. Please try again.</p>
      </div>
    );
  }

  // Show team information if authenticated
  return (
    <div className="space-y-6">
      {/* Organization Settings Section - Visible to all org members */}
      {organizationData && user?.organizationId && (
        <OrganizationSettings
          organizationName={organizationData.organizationName || 'Provisional Organization'}
          organizationId={user.organizationId}
          onUpdate={() => {
            logger.info('🔄 [MANAGE-TEAM-PANEL] Organization name updated, refreshing data');
            refetchChatAccess();
          }}
        />
      )}

      {/* SSO & Domain — Admin only */}
      {canManageUsers && token && (
        <InfoSection title="Security & Access">
          <WorkOsWidgetErrorBoundary
            onRetry={() => {
              logger.info('🔄 [MANAGE-TEAM-PANEL] Error boundary triggered token refresh');
              refresh();
            }}
          >
            <WorkOsWidgets
              theme={{
                hasBackground: false,
                fontFamily: 'Calibre',
              }}
            >
              <AdminPortalSsoConnection authToken={token} />
              <AdminPortalDomainVerification authToken={token} />
            </WorkOsWidgets>
          </WorkOsWidgetErrorBoundary>
        </InfoSection>
      )}

      {/* Message for non-admins */}
      {!canManageUsers && (
        <InfoSection>
          <p className="text-secondary-enhanced">
            Only administrators can configure SSO and domain verification. Contact your organization
            admin to make changes.
          </p>
        </InfoSection>
      )}
    </div>
  );
};

ManageTeamPanel.displayName = 'ManageTeamPanel';

export { ManageTeamPanel };
